import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useAuth from '../../../../features/hooks/useAuth';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
`;

const BasicDetailsCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
  padding: 20px;
  border-top: 4px solid #1976d2;
`;

const DetailsTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #333;
  font-size: 18px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const DetailItem = styled.div`
  margin-bottom: 10px;
`;

const DetailLabel = styled.div`
  font-weight: 500;
  color: #666;
  margin-bottom: 5px;
  font-size: 14px;
`;

const DetailValue = styled.div`
  color: #333;
  font-weight: 500;
`;

const TimelineCard = styled.div`
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 20px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background-color: ${props => {
    if (props.status === 'rejected') return '#fff8f8';
    if (props.status === 'accepted' || props.status === 'completed') return '#f1f8e9';
    if (props.isLast && (props.isRequestClosed || props.isRequestCompleted)) return '#f1f8e9';
    return props.isEven ? '#f8f9fa' : '#fff';
  }};
  border-left: 3px solid ${props => {
    if (props.status === 'pending') return '#f57c00';
    if (props.status === 'rejected') return '#d32f2f';
    if (props.status === 'accepted' || props.status === 'completed') return '#4caf50';
    if (props.isLast && (props.isRequestClosed || props.isRequestCompleted)) return '#4caf50';
    return '#388e3c';
  }};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.1);
  }
`;

const TimelineContainer = styled.div`
  margin-top: 24px;
  padding-left: 16px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 2px;
    background-color: #e0e0e0;
    z-index: 0;
  }
`;

const TimelineItemContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 24px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #1976d2;
    z-index: 1;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const TimelineTitle = styled.div`
  font-size: 17px;
  font-weight: 500;
  color: #202124;
  letter-spacing: -0.01em;
`;

const TimelineTime = styled.div`
  font-size: 14px;
  color: #5f6368;
`;

const ConsumableDetails = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 4px;
`;

const ConsumableTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }
  
  th {
    background-color: #e3f2fd;
    font-weight: 600;
  }
  
  tr:hover {
    background-color: #f5f5f5;
  }
`;

const Remark = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  
  h4 {
    margin: 0 0 10px 0;
    color: #333;
  }
`;

const UserInfo = styled.div`
  margin-top: 15px;
  padding: 10px;
  background: #e3f2fd;
  border-radius: 4px;
  font-size: 14px;
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background: ${props => {
    if (props.status === 'accepted' || props.status === 'closed' || props.status === 'completed') return '#e8f5e9';
    if (props.status === 'pending') return '#fff8f1';
    if (props.status === 'rejected') return '#ffebee';
    return '#f5f5f5';
  }};
  color: ${props => {
    if (props.status === 'accepted' || props.status === 'closed' || props.status === 'completed') return '#2e7d32';
    if (props.status === 'pending') return '#ed6c02';
    if (props.status === 'rejected') return '#d32f2f';
    return '#757575';
  }};
`;

const ConsumableSectionHeader = styled.div`
  background-color: ${props => 
    props.status === 'accepted' ? '#e8f5e9' : 
    props.status === 'pending' ? '#fff8f1' : 
    props.status === 'rejected' ? '#ffebee' :
    '#ffffff'
  };
  padding: 12px;
  border-radius: 4px 4px 0 0;
  margin-top: 16px;
`;

const ConsumableRow = styled.tr`
  &:nth-child(odd) {
    background-color: ${props => {
      if (props.status === 'accepted') return '#f1f8e9';
      if (props.status === 'rejected') return '#fff8f8';
      if (props.status === 'pending') return '#fff8f1';
      return '#f8f9fa';
    }};
  }
  
  &:nth-child(even) {
    background-color: ${props => {
      if (props.status === 'accepted') return '#e8f5e9';
      if (props.status === 'rejected') return '#ffebee';
      if (props.status === 'pending') return '#fff3e0';
      return '#ffffff';
    }};
  }
`;

const UnivBoToStoreFlow = ({ row }) => {
  // User authentication and context
  const { id, designation, designationSuffix, numSuffix, office } = useAuth();
  const user_current_office = useSelector((state) => state?.office?.off || '');
  const user_current_role_array = useSelector((state) => 
    state?.roles?.roles?.[user_current_office] || []
  );

  // State management with validation
  const [requestData, setRequestData] = useState([]);
  const [store_off, setStoreOff] = useState('');

  // Check if the request is closed based on multiple possible indicators
  const isRequestClosed = useMemo(() => {
    // Log the row data to debug console (remove in production)
    console.log("Request data:", row);
    
    // Check various possible indicators of a closed status
    return (
      (row?.curr_status?.toLowerCase?.() === 'closed') ||
      (row?.curr_status?.toLowerCase?.() === 'completed') ||
      // Check for closure tags in the request data
      row?.req_data?.some(item => 
        item.sent_to_tag === 'cosumureq_close' || 
        item.sent_to_tag === 'bo_bts_accept_close' || 
        item.status === 'closed' || 
        item.status === 'completed' || 
        item.action?.toLowerCase?.().includes('closed') ||
        item.action?.toLowerCase?.().includes('completed')
      )
    );
  }, [row]);

  return (
    <Container>
      {/* Basic Details Section */}
      <BasicDetailsCard>
        <DetailsTitle>Request Details</DetailsTitle>
        <DetailsGrid>
          {/* Debug output - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{fontSize: '11px', color: '#999', gridColumn: '1 / -1'}}>
              curr_status: {row?.curr_status || 'N/A'}, 
              status: {row?.status || 'N/A'}, 
              closed: {isRequestClosed ? 'true' : 'false'}
            </div>
          )}
          
          <DetailItem>
            <DetailLabel>Request ID</DetailLabel>
            <DetailValue>{row?.uniqueReqId}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Request Type</DetailLabel>
            <DetailValue>{row?.req_type || "Consumable Issue"}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              <StatusBadge status={
                isRequestClosed ? 'closed' : 
                row?.req_data?.some(item => item.sent_to_tag === 'cosumureq_rejection') ? 'rejected' : 'pending'
              }>
                {isRequestClosed ? (
                  'CLOSED'
                ) : row?.req_data?.some(item => ["cosumureq_rejection"].includes(item.sent_to_tag)) ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CancelOutlinedIcon fontSize="small" /> REJECTED
                  </span>
                ) : 'PENDING'}
              </StatusBadge>
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Created On</DetailLabel>
            <DetailValue>
              {moment(row?.created_at).format('DD/MM/YYYY HH:mm')}
            </DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>Requested Store</DetailLabel>
            <DetailValue>{row?.req_store || 'N/A'}</DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>Requester</DetailLabel>
            <DetailValue>
              {row?.req_creator_details || '-'} 
            </DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>No. of Actions</DetailLabel>
            <DetailValue>{row?.req_data?.length}</DetailValue>
          </DetailItem>
        </DetailsGrid>
        
        {/* Requested Consumables Table */}
        {row?.consumables_requested?.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <DetailLabel style={{ fontSize: '16px', marginBottom: '10px' }}>Requested Consumables</DetailLabel>
            {[...row.consumables_requested].reverse().map((consumableRequest, reqIndex) => {
              // Find the corresponding action in req_data based on timestamp
              const actionIndex = row.req_data.findIndex(action => {
                const actionTime = new Date(action.time_stamp).getTime();
                const requestTime = new Date(consumableRequest.time_stamp).getTime();
                return Math.abs(actionTime - requestTime) < 1000; // Within 1 second
              });
              const actionNumber = actionIndex !== -1 ? actionIndex + 1 : consumableRequest.actionNo || '-';
              
              return (
              <div key={reqIndex} style={{ marginBottom: '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd',
                  borderRadius: '4px 4px 0 0',
                  marginBottom: '0'
                }}>
                  <div><strong>Reference:</strong> Action {actionNumber}</div>
                  <div>
                    <strong>{reqIndex === row.consumables_requested.length - 1 ? "Requested on: " : "Modification Requested on: "}</strong>
                    {moment(consumableRequest.time_stamp).format('DD/MM/YYYY HH:mm')}
                  </div>
                </div>
                <ConsumableTable>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>No.</th>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>Consumable</th>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>Asset</th>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>Asset Tag</th>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>Part No</th>
                      <th style={{ backgroundColor: reqIndex === 0 ? '#f1f8e9' : '#e3f2fd' }}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consumableRequest?.data?.map((item, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{item?.consumable || '-'}</td>
                        <td>{item?.asset || '-'}</td>
                        <td>{item?.assettag?.label || '-'}</td>
                        <td>{item?.partno || '-'}</td>
                        <td>{item?.quantity || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </ConsumableTable>
              </div>
            )})}
          </div>
        )}

        {/* BO Partial Issue Consumables Section */}
        {row?.bo_consum_issue?.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <DetailLabel style={{ fontSize: '16px', marginBottom: '10px' }}>Consumables Issued at BO Level</DetailLabel>
            
            {[...row.bo_consum_issue].reverse().map((partialIssue, idx) => {
              // Check if this is the final object and the request is closed/completed
              const isFinalIssue = idx === 0;
              const isRequestFinalized = isRequestClosed || 
                row?.req_data?.some(item => 
                  item.sent_to_tag === 'bo_bts_accept_close' || 
                  item.curr_status?.toLowerCase?.() === 'completed'
                );
              
              // Determine display status - show as accepted if it's the final issue and request is accepted
              const displayStatus = (isFinalIssue && isRequestFinalized) ? 'accepted' : (partialIssue?.status || 'draft');
              
              // Find the corresponding action in req_data based on timestamp
              const actionIndex = row.req_data.findIndex(action => {
                const actionTime = new Date(action.time_stamp).getTime();
                const issueTime = new Date(partialIssue.time_stamp).getTime();
                return Math.abs(actionTime - issueTime) < 1000; // Within 1 second
              });
              const actionNumber = actionIndex !== -1 ? actionIndex + 1 : (row.bo_consum_issue.length - idx);
              
              return (
              <div key={idx} style={{ marginBottom: '20px' }}>
                <ConsumableSectionHeader status={displayStatus} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  marginBottom: '0'
                }}>
                  <div><strong>Reference:</strong> Action {actionNumber}</div>
                  <div>
                    <strong>{idx === row.bo_consum_issue.length - 1 ? "Issued on: " : "Modified on: "}</strong>
                    {moment(partialIssue?.time_stamp).format('DD/MM/YYYY HH:mm')}
                    {(displayStatus === 'accepted') && (
                      <span style={{ color: '#2e7d32', marginLeft: '15px' }}>
                        • Accepted on: {moment(row?.req_data?.find(item => item.sent_to_tag === 'bo_bts_accept_close')?.time_stamp || new Date()).format('DD/MM/YYYY HH:mm')}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={displayStatus}>
                    {(displayStatus).toUpperCase()}
                  </StatusBadge>
                </ConsumableSectionHeader>
                
                <ConsumableTable>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Sl.No.</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Consumable</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Part No</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Asset (if applicable)</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Asset Tag</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partialIssue.data?.map((item, itemIdx) => (
                      <ConsumableRow key={itemIdx} status={displayStatus}>
                        <td>{itemIdx + 1}</td>
                        <td>{item?.consumable || '-'}</td>
                        <td>{item?.partno || '-'}</td>
                        <td>{item?.asset || '-'}</td>
                        <td>{item?.assettag?.label || '-'}</td>
                        <td>{item?.quantity || '-'}</td>
                      </ConsumableRow>
                    ))}
                  </tbody>
                </ConsumableTable>
              </div>
            )})}
          </div>
        )}

        {/* Store Consumables Issue Section */}
        {row?.store_consum_issue?.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <DetailLabel style={{ fontSize: '16px', marginBottom: '10px' }}>Consumables Issued by Store</DetailLabel>
            
            {[...row.store_consum_issue].reverse().map((storeIssue, idx) => {
              // Check if this is the final object and the request is closed/completed
              const isFinalIssue = idx === 0;
              const isRequestFinalized = isRequestClosed || 
                row?.req_data?.some(item => 
                  item.sent_to_tag === 'bo_bts_accept_close' || 
                  item.curr_status?.toLowerCase?.() === 'completed'
                );
              
              // Determine display status - show as accepted if it's the final issue and request is accepted
              const displayStatus = (isFinalIssue && isRequestFinalized) ? 'accepted' : (storeIssue?.status || 'draft');
              
              // Find the corresponding action in req_data based on timestamp
              const actionIndex = row.req_data.findIndex(action => {
                const actionTime = new Date(action.time_stamp).getTime();
                const issueTime = new Date(storeIssue.time_stamp).getTime();
                return Math.abs(actionTime - issueTime) < 1000; // Within 1 second
              });
              const actionNumber = actionIndex !== -1 ? actionIndex + 1 : (row.store_consum_issue.length - idx);
              
              return (
              <div key={idx} style={{ marginBottom: '20px' }}>
                <ConsumableSectionHeader status={displayStatus} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  marginBottom: '0'
                }}>
                  <div><strong>Reference:</strong> Action {actionNumber}</div>
                  <div>
                    <strong>{idx === row.store_consum_issue.length - 1 ? "Issued on: " : "Modified on: "}</strong>
                    {moment(storeIssue?.time_stamp).format('DD/MM/YYYY HH:mm')}
                    {(displayStatus === 'accepted') && (
                      <span style={{ color: '#2e7d32', marginLeft: '15px' }}>
                        • Accepted on: {moment(row?.req_data?.find(item => item.sent_to_tag === 'bo_bts_accept_close')?.time_stamp || new Date()).format('DD/MM/YYYY HH:mm')}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={displayStatus}>
                    {(displayStatus).toUpperCase()}
                  </StatusBadge>
                </ConsumableSectionHeader>
                
                <ConsumableTable>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Sl.No.</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Consumable</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Part No</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Asset (if applicable)</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Asset Tag</th>
                      <th style={{ backgroundColor: displayStatus === 'accepted' ? '#e8f5e9' : '#fff8f1' }}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storeIssue.data?.map((item, itemIdx) => (
                      <ConsumableRow key={itemIdx} status={displayStatus}>
                        <td>{itemIdx + 1}</td>
                        <td>{item?.consumable || '-'}</td>
                        <td>{item?.partno || '-'}</td>
                        <td>{item?.asset || '-'}</td>
                        <td>{item?.assettag?.label || '-'}</td>
                        <td>{item?.quantity || '-'}</td>
                      </ConsumableRow>
                    ))}
                  </tbody>
                </ConsumableTable>
              </div>
            )})}
          </div>
        )}
      </BasicDetailsCard>

      <DetailsTitle>Request Timeline</DetailsTitle>
      <TimelineContainer>
        {row?.req_data?.map((entry, index) => (
          <TimelineItemContainer key={index}>
            <TimelineCard 
              isEven={index % 2 === 1} 
              isLast={index === row?.req_data?.length - 1}
              isRequestClosed={row?.curr_status === 'Closed'}
              isRequestCompleted={row?.curr_status === 'Completed'}
              status={
                entry?.sent_to_tag === 'cosumureq_rejection' ? 'rejected' : 
                entry?.sent_to_tag === 'cosumureq_close' || 
                entry?.sent_to_tag === 'bo_bts_accept_close' ||
                entry?.curr_status?.toLowerCase() === 'closed' ||
                entry?.curr_status?.toLowerCase() === 'completed' ? 'completed' :
                'pending'
              }
            >
              <TimelineHeader>
                <TimelineTitle>Action {index + 1}</TimelineTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <TimelineTime>
                    {moment(entry?.time_stamp).format('DD/MM/YYYY HH:mm')}
                  </TimelineTime>
                  <StatusBadge 
                    status={
                      entry?.sent_to_tag === 'cosumureq_rejection' ? 'rejected' : 
                      entry?.sent_to_tag === 'cosumureq_close' || 
                      entry?.sent_to_tag === 'bo_bts_accept_close' ||
                      entry?.curr_status?.toLowerCase() === 'closed' ||
                      entry?.curr_status?.toLowerCase() === 'completed' ? 'accepted' :
                      'pending'
                    }
                  >
                    {entry?.sent_to_tag === 'cosumureq_rejection' ? 'REJECTED' : 
                     entry?.sent_to_tag === 'cosumureq_close' || 
                     entry?.sent_to_tag === 'bo_bts_accept_close' ||
                     entry?.curr_status?.toLowerCase() === 'closed' ||
                     entry?.curr_status?.toLowerCase() === 'completed' ? 'ACCEPTED/CLOSED' :
                     'PENDING'}
                  </StatusBadge>
                </div>
              </TimelineHeader>

              {entry?.action && (
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '10px 12px', 
                  background: entry?.sent_to_tag === 'cosumureq_rejection' ? '#ffebee' : 
                             entry?.sent_to_tag === 'cosumureq_close' || 
                             entry?.sent_to_tag === 'bo_bts_accept_close' ||
                             entry?.curr_status?.toLowerCase() === 'closed' ||
                             entry?.curr_status?.toLowerCase() === 'completed' ? '#e8f5e9' : 
                             '#f0f8ff', 
                  borderRadius: '4px',
                  borderLeft: `3px solid ${
                    entry?.sent_to_tag === 'cosumureq_rejection' ? '#d32f2f' : 
                    entry?.sent_to_tag === 'cosumureq_close' || 
                    entry?.sent_to_tag === 'bo_bts_accept_close' ||
                    entry?.curr_status?.toLowerCase() === 'closed' ||
                    entry?.curr_status?.toLowerCase() === 'completed' ? '#4caf50' : 
                    '#1976d2'
                  }`,
                  fontSize: '16px',
                  lineHeight: '1.5',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {entry.action}
                </div>
              )}

              <Remark>
                <h4>Remark</h4>
                <div dangerouslySetInnerHTML={{ __html: entry?.Text || '' }} />
              </Remark>

              <UserInfo>
                <div style={{ marginBottom: '5px' }}>
                  <strong>From:</strong> {entry?.sent_by?.name?.firstName || '-'} - {entry?.sent_by?.office || '-'} - {entry?.sent_by?.designation || '-'}
                </div>
                <div>
                  <strong>To:</strong> {entry?.sent_to?.name?.firstName || '-'} - {entry?.sent_to?.office || '-'} - {entry?.sent_to?.designation || '-'}
                </div>
              </UserInfo>
            </TimelineCard>
          </TimelineItemContainer>
        ))}
      </TimelineContainer>
    </Container>
  );
};

export default UnivBoToStoreFlow;